import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { AutomationRule } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import {
  Card,
  CardHeader,
  DataTable,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton } from "../../ui/skeletons";
import {
  delayLabel,
  threadPurposeLabels,
  triggerEventLabel,
  useAutomationRules,
  useEmailTemplates,
} from "./queries";

// A rule is what the platform sends without anybody pressing a button, so this screen is
// deliberately readable by everyone who sends mail and writable only by an administrator:
// the person answering a customer needs to know what that customer was already told.
export function AutomationPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const canManage = membershipAllows(auth.session?.selectedMembership, "integrations.manage");
  const rules = useAutomationRules();
  const templates = useEmailTemplates();
  const [editing, setEditing] = useState<AutomationRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/messaging/automation-rules", {
        method: "POST",
        body: jsonBody({
          id: editing?.id ?? "",
          name: String(form.get("name")),
          triggerEvent: String(form.get("triggerEvent")),
          delayMinutes: Number(form.get("delayMinutes")),
          templateKey: String(form.get("templateKey")),
          purpose: String(form.get("purpose")),
          active: form.get("active") === "on",
          // The version the editor was shown travels with the change, so two
          // administrators editing the same rule cannot silently overwrite each other.
          version: editing?.version ?? 0,
        }),
      });
      setEditing(null);
      setCreating(false);
      await queryClient.invalidateQueries({ queryKey: ["messaging", "automation-rules"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (rules.isLoading)
    return (
      <DetailPageSkeleton title="Email automation" backTo="/emails" backLabel="Back to emails" />
    );
  if (rules.error) return <ErrorPanel error={rules.error} />;

  const items = rules.data?.items ?? [];
  const triggers = rules.data?.triggerEvents ?? [];
  const editorOpen = creating || editing !== null;

  return (
    <EditorPage
      title="Email automation"
      description="What the platform sends on its own. Every automatic message is a rule you can retarget, delay, or switch off, including the ones that were here before you opened this screen."
      backTo="/emails"
      backLabel="Back to emails"
    >
      {error ? <ErrorPanel error={error} /> : null}
      <div className="stack">
        <Card>
          <CardHeader
            title="Rules"
            description="A rule reacts to something that happened, waits if you asked it to, and sends one template to the customer it concerns."
            actions={
              canManage ? (
                <button
                  className="button primary small"
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setCreating(true);
                  }}
                >
                  <Plus size={16} aria-hidden="true" /> Add rule
                </button>
              ) : null
            }
          />
          {items.length ? (
            <DataTable
              caption="Automatic email rules"
              items={items}
              getKey={(rule) => rule.id}
              defaultSort={{ label: "Trigger", direction: "ascending" }}
              columns={[
                {
                  label: "Rule",
                  sortValue: (rule) => rule.name.toLowerCase(),
                  render: (rule) => <strong>{rule.name}</strong>,
                },
                {
                  label: "Trigger",
                  sortValue: (rule) => rule.triggerEvent,
                  render: (rule) => triggerEventLabel(rule.triggerEvent),
                },
                {
                  label: "When",
                  sortValue: (rule) => rule.delayMinutes,
                  render: (rule) => delayLabel(rule.delayMinutes),
                },
                { label: "Template", render: (rule) => rule.templateKey },
                {
                  label: "Purpose",
                  render: (rule) => threadPurposeLabels[rule.purpose] ?? rule.purpose,
                },
                {
                  label: "Status",
                  sortValue: (rule) => (rule.active ? 0 : 1),
                  render: (rule) => <StatusPill value={rule.active ? "active" : "disabled"} />,
                },
                {
                  label: "Options",
                  render: (rule) =>
                    canManage ? (
                      <button
                        className="button secondary small"
                        type="button"
                        onClick={() => {
                          setCreating(false);
                          setEditing(rule);
                        }}
                      >
                        <Pencil size={15} aria-hidden="true" /> Edit
                      </button>
                    ) : (
                      <span className="muted">View only</span>
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No automatic email"
              description="Nothing is sent on its own for this workspace yet."
            />
          )}
          {canManage ? null : (
            <Notice tone="info">
              Changing what the platform sends automatically is an administrator's decision. You can
              see every rule here so you know what a customer has already been told.
            </Notice>
          )}
        </Card>
        {editorOpen && canManage ? (
          <Card>
            <CardHeader
              title={editing ? "Edit rule" : "New rule"}
              description="A rule that is switched off keeps its history. There is no delete, because a rule that sent mail last month is part of the explanation for what a customer received."
            />
            <form className="form-grid" onSubmit={save} key={editing?.id ?? "new-rule"}>
              <Field label="Name">
                <input name="name" required maxLength={120} defaultValue={editing?.name} />
              </Field>
              <Field label="Trigger" hint="The published event this rule reacts to.">
                <select name="triggerEvent" defaultValue={editing?.triggerEvent ?? triggers[0]}>
                  {triggers.map((trigger) => (
                    <option key={trigger} value={trigger}>
                      {triggerEventLabel(trigger)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Delay in minutes"
                hint="Zero sends with the event. Anything longer is scheduled and can be canceled before it goes. One year is the ceiling."
              >
                <input
                  name="delayMinutes"
                  type="number"
                  min={0}
                  max={525600}
                  defaultValue={editing?.delayMinutes ?? 0}
                />
              </Field>
              <Field
                label="Template"
                hint="The wording that is sent. Its category has to match the purpose below."
              >
                <select name="templateKey" defaultValue={editing?.templateKey}>
                  {(templates.data?.items ?? [])
                    .filter((template) => template.active)
                    .map((template) => (
                      <option key={template.key} value={template.key}>
                        {template.key}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Purpose">
                <select name="purpose" defaultValue={editing?.purpose ?? "service"}>
                  <option value="service">Service</option>
                  <option value="marketing">Marketing</option>
                </select>
              </Field>
              <label className="check-field">
                <input name="active" type="checkbox" defaultChecked={editing?.active ?? true} />{" "}
                This rule is switched on
              </label>
              <div className="full">
                <FormActions
                  submitting={working}
                  submitLabel={editing ? "Save rule" : "Add rule"}
                  onCancel={() => {
                    setEditing(null);
                    setCreating(false);
                  }}
                />
              </div>
            </form>
          </Card>
        ) : null}
      </div>
    </EditorPage>
  );
}
